import { memo } from 'react';
import { Label, Select } from 'semantic-ui-react';
import ScaleUtil from '../../../../../../../../../utils/scaleUtil';
import { useRowBoundingBox } from '../../../../../../stores/boundingBoxesStore';
import { ImportDescriptorDataTypeType, ImportDescriptorIdType } from '../../../../../../types';
import { getOptions } from '../../../../../../utils/tableExtraction';
import { ImageHighlighterWrapper } from '../../../../../imageHighlighter/imageHighlighter';
import {
    useImportTableStoreDescriptorsEditMode,
    useImportTableStoreDescriptor,
    useImportTableStoreDescriptorActions,
    useImportTableStoreReportsFieldValue,
} from '../../../../context/importTableContext';
import { getScaleLabelColor, getSelectScaleStyling } from './utils';
import { FIELD_VALUE_SOURCE } from '../../../../../../constants/generalConstants';
import ObjCheck from '../../../../../../../../../utils/objCheck';

type DescriptorScaleCellPropsType = {
    generalScale: number;
    descriptorId: ImportDescriptorIdType;
};
const DescriptorScaleCell = memo(function DescriptorScaleCell({
    generalScale,
    descriptorId,
}: DescriptorScaleCellPropsType) {
    const descriptor = useImportTableStoreDescriptor(descriptorId);
    const editing = useImportTableStoreDescriptorsEditMode();
    const { updateDescriptorScale, enableDescriptorsEditMode } = useImportTableStoreDescriptorActions();
    const rowBoundingBox = useRowBoundingBox(descriptor.id);
    const reportsFieldValue = useImportTableStoreReportsFieldValue(descriptorId);
    const disabled = reportsFieldValue.some(
        (fieldValue) =>
            fieldValue.source === FIELD_VALUE_SOURCE.MASTER_TABLE &&
            !ObjCheck.isNullUndefinedOrEmpty(fieldValue.normalizedValue)
    );
    return (
        <td onDoubleClick={enableDescriptorsEditMode} className={editing ? 'cell--editing' : ''}>
            <ImageHighlighterWrapper boundingBox={rowBoundingBox}>
                {editing ? (
                    <EditDescriptorScale
                        descriptorDataType={descriptor.dataType}
                        tableScale={generalScale}
                        value={descriptor.inputScale.value}
                        handleChange={(data) => updateDescriptorScale(descriptor.id, data)}
                        disabled={disabled}
                    />
                ) : (
                    <ReadDescriptorScale
                        scale={descriptor.inputScale.value}
                        generalScale={generalScale}
                        dataType={descriptor.dataType}
                    />
                )}
            </ImageHighlighterWrapper>
        </td>
    );
});

export default DescriptorScaleCell;

const scalesOPtions = getOptions(ScaleUtil.getScalePairsValueToLabel({ labelShortForm: true, excludeDefault: true }));

type EditDescriptorScalePropsType = {
    value: number;
    handleChange: (data: number) => void;
    tableScale: number;
    descriptorDataType: ImportDescriptorDataTypeType;
    disabled?: boolean;
};

function EditDescriptorScale({
    value,
    handleChange,
    tableScale,
    descriptorDataType,
    disabled,
}: EditDescriptorScalePropsType) {
    const { classes } = getSelectScaleStyling(tableScale, value, descriptorDataType);
    return (
        <Select
            disabled={disabled}
            className={classes}
            placeholder="Scale"
            value={value}
            options={scalesOPtions}
            onChange={(__, data) => handleChange(data.value as number)}
        />
    );
}

type ReadDescriptorScalePropsType = {
    generalScale: number;
    scale: number;
    dataType: ImportDescriptorDataTypeType;
};

function ReadDescriptorScale({ scale, generalScale, dataType }: ReadDescriptorScalePropsType) {
    return (
        <Label className="import-table__label" size="mini" color={getScaleLabelColor(generalScale, scale, dataType)}>
            {ScaleUtil.numToTextRepresentation(scale, true).toUpperCase()}
        </Label>
    );
}
